// Synced Settings preferences (#480 Preference persistence).
//
// Language, preferred display currency, the explicitly chosen reference timezone
// and the Recently-Deleted retention policy used to live ONLY in this browser's
// localStorage, so a change in Chrome never reached Safari or the phone. The
// SERVER is now the source of truth (users.notification_prefs.preferences, read
// back on GET /auth/profile as `preferences`, written through PATCH /auth/profile);
// localStorage is only a cache that keeps the first paint instant and the
// existing `omnis:*-changed` broadcast events working.
//
//   • On every profile read, each server value is ADOPTED into the cache (and
//     broadcast with `source: 'server'`, so the language listener does not push
//     it straight back).
//   • A value the server has never seen but this browser has is MIGRATED up
//     once — nobody loses the setting they already had.
//   • A Settings change writes the cache optimistically, PATCHes, and is reverted
//     (broadcast with `source: 'revert'`) when the save fails.
//
// Purely device-UI state (panel sizes, collapsed sections, the suspended theme)
// stays local on purpose — it is not in this table.
import apiService from '../services/apiService';
import { getAuthToken } from './authToken';

export const isValidTimezone = (tz) => {
    if (typeof tz !== 'string' || !tz.trim() || tz.length > 64) return false;
    try {
        // Throws a RangeError for anything that is not a real IANA zone.
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    } catch (_) {
        return false;
    }
};

const RETENTION_VALUES = new Set(['immediately', '7', '30']);

// key (as served in profile.preferences) → how it is cached + broadcast + patched.
export const PREFERENCE_FIELDS = Object.freeze({
    language: {
        storageKey: 'omnis:language', event: 'omnis:language-changed', detailKey: 'language',
        patchKey: 'language', valid: (v) => v === 'en' || v === 'es',
    },
    display_currency: {
        storageKey: 'omnis:displayCurrency', event: 'omnis:currency-changed', detailKey: 'currency',
        patchKey: 'display_currency', valid: (v) => typeof v === 'string' && /^[A-Z]{3}$/.test(v),
    },
    timezone: {
        storageKey: 'omnis:timezone', event: 'omnis:timezone-changed', detailKey: 'timezone',
        patchKey: 'preferred_timezone', valid: isValidTimezone,
        // A cleared pin = auto-detect; listeners always receive a real zone.
        fallback: () => {
            try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (_) { return 'UTC'; }
        },
    },
    retention_policy: {
        storageKey: 'omnis:retention-policy', event: 'omnis:retention-changed', detailKey: 'policy',
        patchKey: 'retention_policy', valid: (v) => RETENTION_VALUES.has(v),
    },
});

// ── Race guards ──────────────────────────────────────────────────────────────
// A profile read that is in flight while the user changes a preference (switch
// language, then open Settings at once) must not "adopt" the server's OLD value
// over the new one. So adoption skips a key that has a save still in flight, or a
// local change newer than the moment the profile was requested.
const pendingSaves = new Map();      // key → in-flight save count
const lastLocalWrite = new Map();    // key → ms timestamp of the last local (non-server) change

export function trackPreferenceSave(key, promise) {
    pendingSaves.set(key, (pendingSaves.get(key) || 0) + 1);
    const done = () => {
        const n = (pendingSaves.get(key) || 1) - 1;
        if (n > 0) pendingSaves.set(key, n); else pendingSaves.delete(key);
    };
    Promise.resolve(promise).then(done, done);
    return promise;
}

if (typeof window !== 'undefined') {
    // Every local change, wherever it comes from (the language provider, a hook,
    // another component), is stamped — only the server's own adoption is not.
    for (const [key, f] of Object.entries(PREFERENCE_FIELDS)) {
        window.addEventListener(f.event, (e) => {
            if (e?.detail?.source !== 'server') lastLocalWrite.set(key, Date.now());
        });
    }
}

const readLocal = (storageKey) => {
    try { return localStorage.getItem(storageKey); } catch (_) { return null; }
};

/** Write (or clear, for null) one cached preference and broadcast it. */
export function writeLocalPreference(key, value, source) {
    const f = PREFERENCE_FIELDS[key];
    if (!f) return;
    try {
        if (value == null || value === '') localStorage.removeItem(f.storageKey);
        else localStorage.setItem(f.storageKey, value);
    } catch (_) { /* private mode — the in-memory state still moves */ }
    try {
        window.dispatchEvent(new CustomEvent(f.event, {
            detail: {
                [f.detailKey]: value || (f.fallback ? f.fallback() : null),
                ...(source ? { source } : {}),
            },
        }));
    } catch (_) { /* ignore */ }
}

/**
 * Server → cache. Every non-null server value replaces a differing local one.
 * Returns the keys that changed locally (handy for tests).
 */
export function adoptServerPreferences(profile, { requestedAt = null } = {}) {
    const prefs = profile && profile.preferences;
    if (!prefs || typeof prefs !== 'object') return [];
    const changed = [];
    for (const [key, f] of Object.entries(PREFERENCE_FIELDS)) {
        const server = prefs[key];
        if (server == null || !f.valid(server)) continue;
        if (pendingSaves.get(key)) continue;   // our own newer value is on its way
        if (requestedAt != null && (lastLocalWrite.get(key) || 0) >= requestedAt) continue;
        if (readLocal(f.storageKey) !== server) {
            writeLocalPreference(key, server, 'server');
            changed.push(key);
        }
    }
    return changed;
}

/**
 * The one-time migration payload: values this browser holds that the server has
 * never been told. An invalid cached value (e.g. a half-typed timezone the old
 * input stored on every keystroke) is dropped instead of being sent.
 */
export function collectLocalOnlyPreferences(profile) {
    const prefs = profile && profile.preferences;
    if (!prefs || typeof prefs !== 'object') return {};
    const patch = {};
    for (const [key, f] of Object.entries(PREFERENCE_FIELDS)) {
        if (prefs[key] != null) continue;
        const local = readLocal(f.storageKey);
        if (local == null || local === '') continue;
        if (f.valid(local)) patch[f.patchKey] = local;
        else {
            try { localStorage.removeItem(f.storageKey); } catch (_) { /* ignore */ }
        }
    }
    return patch;
}

/**
 * Profile-load reconciliation: adopt the server's values, then migrate any
 * local-only ones up. Best-effort — a refused PATCH (an onboarding gate, being
 * offline) is retried on the next profile read. Resolves to the freshest profile.
 */
export async function reconcilePreferences(profile) {
    let current = profile;
    adoptServerPreferences(current);
    const patch = collectLocalOnlyPreferences(current);
    const entries = Object.entries(patch);
    if (!entries.length) return current;
    try {
        const updated = await apiService.patch('/auth/profile', patch);
        if (updated) current = updated;
    } catch (e) {
        // One value the server rejects (400) must not strand the others: retry
        // field by field. A gate refusal fails every one — that's fine.
        if (e?.status === 400 && entries.length > 1) {
            for (const [k, v] of entries) {
                try {
                    const updated = await apiService.patch('/auth/profile', { [k]: v });
                    if (updated) current = updated;
                } catch (_) { /* next profile read */ }
            }
        }
    }
    return current;
}

/**
 * A Settings change: optimistic cache write + server save, reverted on failure.
 * Resolves `true` when the server saved it, `false` when there is no session to
 * save to (signed out — kept locally, migrated on the next sign-in). Rejects
 * with the API error when the save failed (the cache is already reverted).
 */
export async function savePreference(key, value) {
    const f = PREFERENCE_FIELDS[key];
    if (!f) throw new Error(`unknown preference ${key}`);
    const previous = readLocal(f.storageKey);
    writeLocalPreference(key, value);
    if (!getAuthToken()) return false;
    try {
        await trackPreferenceSave(key, apiService.patch('/auth/profile', {
            [f.patchKey]: value == null || value === '' ? null : value,
        }));
        return true;
    } catch (err) {
        writeLocalPreference(key, previous, 'revert');
        throw err;
    }
}
